import {
  Button,
  Pagination,
  Tabs,
  TabsProps,
  Typography,
  DatePicker,
  TimeRangePickerProps,
  Select,
  Card,
  Space,
} from "antd";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import dayjs from "dayjs";
import { ConsumptionTypes, CreditTypes, LogsType } from "dtos/credit.dto";
import { RoleTypes } from "dtos/user.dto";
import DashboardLayout from "layout/DashboardLayout";
import React, { useEffect, useState } from "react";
import {
  TbArrowDown,
  TbArrowUp,
  TbArrowUpToArc,
  TbLink,
  TbLoader2,
  TbReload,
  TbFilter,
  TbSortDescending,
  TbSortAscending,
  TbRecharging,
} from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import { useAppSelector } from "store";
import { formatMoney } from "utils/functions";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import RechargeCreditsModal from "components/RechargeCreditsModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const zones = [
  { label: "System", value: "system" },
  { label: "IST", value: "Asia/Kolkata" },
  { label: "EST", value: "America/New_York" },
  { label: "UTC", value: "UTC" },
];

const { RangePicker } = DatePicker;

const Credits = () => {
  const [creditType, setCreditType] = useState<CreditTypes>(CreditTypes.AI_AUDIT);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    startDate?: string;
    endDate?: string;
    consumptionType?: ConsumptionTypes;
    sortOrder?: "asc" | "desc";
  }>({
    page: 1,
    limit: 10,
    sortOrder: "desc",
  });

  const metrics = ApiService.credit.useGetMetrics();
  const metricData = metrics.data?.result ?? [];
  const logs = ApiService.credit.useGetLogs(creditType, filters);
  const logsData = logs.data?.result.data ?? [];
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();
  const [openRechargeModal, setOpenRechargeModal] = useState(false);

  const systemZone = dayjs.tz.guess();
  const [zone, setZone] = useState("system");
  const [time, setTime] = useState(dayjs());

  useEffect(() => {
    const i = setInterval(() => setTime(dayjs()), 1000);
    return () => clearInterval(i);
  }, []);

  const resolvedZone = zone === "system" ? systemZone : zone;
  const displayTime = time.tz(resolvedZone).format("YYYY-MM-DD HH:mm:ss");

  useEffect(() => {
    if (!Object.values(user).length) return;

    if (user.role !== RoleTypes.ADMIN && user.role !== RoleTypes.SUPERADMIN) {
      navigate("/dashboard");
    }
  }, [user]);

  const TransactionRow: React.FC<{ transaction: LogsType }> = ({ transaction }) => (
    <div>
      <div className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0">
        <div className="flex items-center space-x-4">
          <div
            className={`w-2 h-12 rounded-full ${transaction.consumption_type !== ConsumptionTypes.USED ? "bg-green-500" : "bg-red-500"}`}
          />
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-lg font-semibold text-gray-900">
                {transaction.consumption_type !== ConsumptionTypes.USED
                  ? `${transaction.consumption_type?.charAt(0).toUpperCase()}${transaction.consumption_type?.slice(1)} of ${formatMoney(transaction.payment)} for ${transaction.amount} credits`
                  : `${-transaction.amount} credit${Math.abs(transaction.amount) > 1 ? "s" : ""}`}
              </span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  transaction.consumption_type !== ConsumptionTypes.USED
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {transaction.consumption_type?.toLocaleUpperCase()}
              </span>
            </div>
            <div className="text-xs font-medium text-gray-900 mb-1">
              {dayjs(new Date(transaction.date)).format("YYYY/MM/DD, HH:mm A")}
            </div>
            {transaction.application && (
              <div className="text-sm font-medium text-gray-900 mb-1">
                Application:{" "}
                <span>
                  <Link to={`/dashboard/application/${transaction.application.app_key}`}>
                    {transaction.application.app_key}
                  </Link>
                </span>
              </div>
            )}
            {transaction.user && (
              <div className="text-sm font-medium text-gray-900 mb-1">
                User: <span>{`${transaction.user.name} (${transaction.user.slug})`}</span>
              </div>
            )}
            <div className="text-sm text-gray-600">{transaction.description}</div>
          </div>
        </div>
        {/* <div className="flex items-center space-x-3">
          <div className="text-right">
            <Button type="primary" ghost icon={<TbLink />}>
              View transactions
            </Button>
          </div>
        </div> */}
      </div>
    </div>
  );

  function colorPicker(amount: number) {
    if (amount < 10) return "text-red-600";
    else if (amount < 40) return "text-yellow-600";
    else return "text-green-600";
  }

  const onChange = (key: string) => {
    if (key === "1") setCreditType(CreditTypes.AI_AUDIT);
    else if (key === "2") setCreditType(CreditTypes.INTEL);
    else if (key === "3") setCreditType(CreditTypes.FORECASTING);
    else if (key === "4") setCreditType(CreditTypes.DEBTOR_SUMMARY);
    else if (key === "5") setCreditType(CreditTypes.CASE_SPLIT);
    else if (key === "6") setCreditType(CreditTypes.NEGOTIATION_MAIL);

    setFilters((prev) => ({
      ...prev,
      page: 1,
      limit: 10,
    }));
  };

  const LogViewer = ({ isPending }: { isPending: boolean }) => (
    <>
      {isPending ? (
        <div className="flex justify-center">
          <TbLoader2 className="animate-spin size-6" color="#6f4fef" />
        </div>
      ) : logsData.length === 0 ? (
        <p className="text-center p-2">No logs found.</p>
      ) : (
        <div className="space-y-0">
          {logsData.map((transaction, idx) => (
            <TransactionRow key={idx} transaction={transaction} />
          ))}
          <Pagination
            className="mt-2"
            onChange={(page, pageSize) => setFilters((prev) => ({ ...prev, limit: pageSize, page }))}
            align="end"
            showSizeChanger
            defaultCurrent={logs.data?.result.page}
            total={logs.data?.result.totalPages ?? 0}
            pageSize={logs.data?.result.limit}
            current={logs.data?.result.page}
            showTotal={(total, range) => {
              return `Showing ${range[0]}-${range[1]} of ${total} results`;
            }}
          />
        </div>
      )}
    </>
  );

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "AI Audit",
      children: <LogViewer isPending={logs.isPending} />,
    },
    {
      key: "2",
      label: "Intel",
      children: <LogViewer isPending={logs.isPending} />,
    },
    {
      key: "3",
      label: "Forecasting",
      children: <LogViewer isPending={logs.isPending} />,
    },
    {
      key: "4",
      label: "Debtor Summary",
      children: <LogViewer isPending={logs.isPending} />,
    },
    {
      key: "5",
      label: "Case Actions",
      children: <LogViewer isPending={logs.isPending} />,
    },
    {
      key: "6",
      label: "Negotiation Mails",
      children: <LogViewer isPending={logs.isPending} />,
    },
  ];
  const onDateChange: TimeRangePickerProps["onCalendarChange"] = (dates) => {
    setFilters((prev) => ({
      ...prev,
      startDate: dates?.[0]?.toISOString(),
      endDate: dates?.[1]?.toISOString(),
    }));
  };

  return (
    <DashboardLayout
      headerRight={
        <div className="flex items-center gap-2">
          <Button
            variant="solid"
            icon={<TbRecharging />}
            onClick={() => {
              setOpenRechargeModal(true);
            }}
            color="green"
          >
            Log Credit Recharge
          </Button>
          <Button
            type="primary"
            icon={<TbReload />}
            disabled={metrics.isPending && logs.isPending}
            onClick={() => {
              metrics.refetch();
              logs.refetch();
            }}
          >
            Refresh
          </Button>
        </div>
      }
    >
      <PageTitle title="Credits" pageTitle={[{ title: "Credits" }]} />
      {openRechargeModal && (
        <RechargeCreditsModal
          open={openRechargeModal}
          onClose={() => setOpenRechargeModal(false)}
          refetch={() => {
            logs.refetch();
            metrics.refetch();
          }}
        />
      )}
      <div className="p-6 bg-gray-50 min-h-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="mb-4 pl-1">Remaining Credits</h2>
          <Space align="center">
            <Typography.Title
              level={4}
              style={{
                margin: 0,
                lineHeight: "32px",
              }}
            >
              {displayTime} ({resolvedZone})
            </Typography.Title>

            <Select style={{ width: 160 }} value={zone} options={zones} onChange={setZone} />
          </Space>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metricData.map((metric, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200"
            >
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{metric.title}</div>
              <div className={`text-3xl font-bold mb-2 ${colorPicker(metric.amount)}`}>{metric.amount}</div>
              {/* {metric.amount >= 1 && metric.amount < 10 && (
                <div className={`text-sm text-red-700 font-bold mb-3`}>Credits running out.</div>
              )}
              {metric.amount === 0 && <div className={`text-sm text-red-700 font-bold mb-3`}>Recharge now.</div>} */}
              {metric.overdue !== 0 && (
                <div className={`text-xs font-medium text-red-400 mb-2`}>
                  Overdue amount:{" "}
                  <span className={`text-red-600 font-semibold`}>{formatMoney(-1 * metric.overdue)} </span>
                </div>
              )}

              <div className="text-xs font-semibold text-gray-500">{metric.used.toLocaleString()} credits used</div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap gap-3 justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>

            <div className="flex flex-wrap gap-3 items-center">
              <Select<ConsumptionTypes | "all">
                style={{ width: 180 }}
                value={filters.consumptionType ?? "all"}
                suffixIcon={<TbFilter />}
                onChange={(val) =>
                  setFilters((prev) => ({
                    ...prev,
                    page: 1,
                    consumptionType: val === "all" ? undefined : val,
                  }))
                }
                options={[
                  {
                    label: (
                      <div className="flex items-center gap-2">
                        <TbFilter size={16} />
                        All Types
                      </div>
                    ),
                    value: "all",
                  },
                  {
                    label: (
                      <div className="flex items-center gap-2 text-red-500">
                        <TbArrowDown size={16} />
                        Used
                      </div>
                    ),
                    value: ConsumptionTypes.USED,
                  },
                  {
                    label: (
                      <div className="flex items-center gap-2 text-green-500">
                        <TbArrowUp size={16} />
                        Recharge
                      </div>
                    ),
                    value: ConsumptionTypes.RECHARGE,
                  },
                  {
                    label: (
                      <div className="flex items-center gap-2 text-blue-500">
                        <TbArrowUpToArc size={16} />
                        Refund
                      </div>
                    ),
                    value: ConsumptionTypes.REFUND,
                  },
                ]}
              />

              <RangePicker
                size="middle"
                variant="outlined"
                placement="topLeft"
                allowEmpty
                onCalendarChange={onDateChange}
                allowClear
                value={[
                  filters.startDate ? dayjs(filters.startDate) : null,
                  filters.endDate ? dayjs(filters.endDate) : null,
                ]}
              />

              <Button
                icon={filters.sortOrder === "desc" ? <TbSortDescending size={18} /> : <TbSortAscending size={18} />}
                onClick={() =>
                  setFilters((prev) => ({
                    ...prev,
                    page: 1,
                    sortOrder: prev.sortOrder === "desc" ? "asc" : "desc",
                  }))
                }
              />
            </div>
          </div>

          <div className="px-6 pb-6">
            <Tabs tabBarGutter={50} defaultActiveKey="1" items={items} onChange={onChange} />
          </div>

          {/* Yesterday */}
          {/* <div className="px-6 pb-6">
            <div className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">YESTERDAY</div>
            <div className="space-y-0">
              {yesterdayTransactions.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </div>
          </div> */}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Credits;
